/**
 * @module app/useCauseActions
 * @description The actions every cause surface offers (card, featured card,
 * Quick View): add to cart, donate now, share, save. One implementation,
 * many callers (DRY).
 */
import { useCallback } from "react";
import { DEFAULT_GIFT_USD } from "../config/page";
import { FEATURES } from "../config/tier";
import type { Cause } from "../domain/types";
import { formatUsd } from "../lib/format";
import { useGiving } from "./GivingContext";
import { navigate } from "./router";

/** Bound actions for cause UI. */
export interface CauseActions {
    addToCart: (cause: Cause, amountUsd?: number) => void;
    donateNow: (cause: Cause) => void;
    share: (cause: Cause) => Promise<void>;
    toggleSaved: (cause: Cause) => void;
    isSaved: (cause: Cause) => boolean;
    isInCart: (cause: Cause) => boolean;
}

/** @returns cause actions bound to the giving state. */
export function useCauseActions(): CauseActions {
    const { dispatchCart, showToast, saved, toggleSaved, lines, setCartOpen } = useGiving();

    const addToCart = useCallback((cause: Cause, amountUsd: number = DEFAULT_GIFT_USD): void => {
        dispatchCart({ type: "add", causeId: cause.id, amountUsd });
        showToast(`Added ${formatUsd(amountUsd)} for “${cause.title}”. You can edit it in your cart.`);
    }, [dispatchCart, showToast]);

    // Donate = add (if missing) and open the cart, so the donor still sees one checkout.
    const donateNow = useCallback((cause: Cause): void => {
        if (!lines.some((l) => l.causeId === cause.id)) dispatchCart({ type: "add", causeId: cause.id, amountUsd: DEFAULT_GIFT_USD });
        setCartOpen(false);
        navigate("checkout");
    }, [dispatchCart, lines, setCartOpen]);

    // Native share sheet where supported (mobile); otherwise copy the link.
    const share = useCallback(async (cause: Cause): Promise<void> => {
        const url: string = `${window.location.origin}${window.location.pathname}#/`;
        const data: ShareData = { title: cause.title, text: `Support ${cause.partner} through The American Friends of Jamaica.`, url };
        try {
            if (typeof navigator.share === "function") {
                await navigator.share(data);
                return;
            }
            await navigator.clipboard.writeText(url);
            showToast("Link copied. Share it anywhere.");
        } catch {
            // The donor dismissed the share sheet or clipboard was blocked: nothing to report.
        }
    }, [showToast]);

    const toggle = useCallback((cause: Cause): void => {
        const wasSaved: boolean = saved.includes(cause.id);
        toggleSaved(cause.id);
        showToast(wasSaved ? "Removed from saved causes."
            : FEATURES.savedCausesPersist ? "Saved on this device, even after you leave. Find it under Saved." : "Saved for this visit. Find it under Saved.");
    }, [saved, toggleSaved, showToast]);

    return {
        addToCart,
        donateNow,
        share,
        toggleSaved: toggle,
        isSaved: (cause: Cause) => saved.includes(cause.id),
        isInCart: (cause: Cause) => lines.some((l) => l.causeId === cause.id),
    };
}
